import React, { useRef, useEffect } from 'react';

const drawMiniChart = (canvas, data, getSeries, { color, fillColor, label, unit, maxOverride, period, formatVal, thresholds }) => {
  if (!canvas || !data || data.length === 0) return;

  const ctx = canvas.getContext('2d');
  const dpr = window.devicePixelRatio || 1;
  const W = canvas.clientWidth;
  const H = canvas.clientHeight;
  canvas.width = W * dpr;
  canvas.height = H * dpr;
  ctx.scale(dpr, dpr);

  const pad = { top: 8, right: 10, bottom: 24, left: 40 };
  const chartW = W - pad.left - pad.right;
  const chartH = H - pad.top - pad.bottom;

  ctx.clearRect(0, 0, W, H);

  // Get values
  const vals = data.map(d => getSeries(d)).filter(v => v != null);
  if (vals.length === 0) return;

  const dataMax = Math.max(...vals);
  const maxVal = maxOverride || Math.max(dataMax * 1.2, 1);

  // Grid
  ctx.strokeStyle = '#f1f5f9';
  ctx.lineWidth = 0.5;
  ctx.font = '9px system-ui, sans-serif';
  ctx.fillStyle = '#94a3b8';
  ctx.textAlign = 'right';
  for (let i = 0; i <= 3; i++) {
    const y = pad.top + (chartH / 3) * i;
    ctx.beginPath();
    ctx.moveTo(pad.left, y);
    ctx.lineTo(pad.left + chartW, y);
    ctx.stroke();
    const v = maxVal - (maxVal / 3) * i;
    ctx.fillText(formatVal ? formatVal(v) : v.toFixed(v >= 10 ? 0 : 1), pad.left - 4, y + 3);
  }

  // Time labels
  const labelCount = Math.min(6, data.length);
  const step = Math.max(1, Math.floor(data.length / labelCount));
  ctx.textAlign = 'center';
  ctx.fillStyle = '#94a3b8';
  for (let i = 0; i < data.length; i += step) {
    const x = pad.left + (i / (data.length - 1 || 1)) * chartW;
    const ts = data[i].timestamp;
    if (!ts) continue;
    const d = new Date(ts);
    const lbl = (period === '7d' || period === '30d')
      ? d.toLocaleDateString('ru', { day: '2-digit', month: '2-digit' })
      : d.toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' });
    ctx.fillText(lbl, x, pad.top + chartH + 14);
  }

  // Draw line
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.lineJoin = 'round';
  ctx.beginPath();
  let started = false;
  let lastX = pad.left, lastY = pad.top + chartH;
  for (let i = 0; i < data.length; i++) {
    const val = getSeries(data[i]);
    if (val == null) continue;
    const x = pad.left + (i / (data.length - 1 || 1)) * chartW;
    const y = pad.top + chartH - (val / maxVal) * chartH;
    if (!started) { ctx.moveTo(x, y); started = true; }
    else ctx.lineTo(x, y);
    lastX = x; lastY = y;
  }
  ctx.stroke();

  // Fill gradient
  const grad = ctx.createLinearGradient(0, pad.top, 0, pad.top + chartH);
  grad.addColorStop(0, fillColor || (color + '25'));
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.lineTo(lastX, pad.top + chartH);
  ctx.lineTo(pad.left, pad.top + chartH);
  ctx.closePath();
  ctx.fillStyle = grad;
  ctx.fill();

  // Threshold lines
  if (thresholds) {
    thresholds.forEach(th => {
      if (th.value > maxVal) return;
      const y = pad.top + chartH - (th.value / maxVal) * chartH;
      ctx.setLineDash([4, 4]);
      ctx.strokeStyle = th.color || '#ef4444';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(pad.left, y);
      ctx.lineTo(pad.left + chartW, y);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.font = '8px system-ui';
      ctx.fillStyle = th.color || '#ef4444';
      ctx.textAlign = 'left';
      ctx.fillText(th.label || '', pad.left + chartW - ctx.measureText(th.label || '').width - 2, y - 3);
    });
  }
};

const MiniChartCard = ({ title, value, subtitle, color, bgColor, borderColor, data, getSeries, unit, maxOverride, period, formatVal, thresholds, icon }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    drawMiniChart(canvasRef.current, data, getSeries, { color, fillColor: color + '20', label: title, unit, maxOverride, period, formatVal, thresholds });
  }, [data, period]);

  return (
    <div style={{
      background: bgColor || '#fff',
      borderRadius: '14px',
      padding: '14px 16px 8px',
      border: `1px solid ${borderColor || '#e2e8f0'}`,
      display: 'flex',
      flexDirection: 'column',
      gap: '4px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: '0.72rem', color: color, fontWeight: 600, letterSpacing: '0.02em', textTransform: 'uppercase' }}>{title}</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.2, marginTop: '2px' }}>{value}<span style={{ fontSize: '0.8rem', fontWeight: 500, color: '#64748b', marginLeft: '3px' }}>{unit}</span></div>
          {subtitle && <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '1px' }}>{subtitle}</div>}
        </div>
        {icon && <div style={{ opacity: 0.15, fontSize: '2rem' }}>{icon}</div>}
      </div>
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height: '80px', display: 'block', borderRadius: '6px', marginTop: '4px' }}
      />
    </div>
  );
};

const MonitorChart = ({ data, period, monitorData, bandwidthMbps = 500 }) => {
  if (!data || data.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8', fontSize: '0.85rem' }}>
        Данные начнут появляться через 1-2 минуты после запуска мониторинга
      </div>
    );
  }

  const isHourly = data[0]?.type === 'hourly';
  const getCpu = d => isHourly ? d.cpu?.avg : d.cpu?.total_percent;
  const getRam = d => isHourly ? d.memory?.avg_percent : d.memory?.percent;
  const getNetOut = d => isHourly ? d.network?.avg_out_mbps : d.network?.out_mbps;
  const getNetIn = d => isHourly ? d.network?.avg_in_mbps : d.network?.in_mbps;
  const getConns = d => isHourly ? d.connections?.avg : d.connections?.http_active;
  const getStreams = d => isHourly ? (d.connections?.avg_streams || 0) : (d.connections?.estimated_1080p_streams || 0);
  const getDisk = d => isHourly ? d.disk?.avg_percent : d.disk?.percent;

  // Current values from monitorData (realtime)
  const cpu = monitorData?.cpu?.total_percent || 0;
  const ram = monitorData?.memory?.percent || 0;
  const ramGb = monitorData?.memory?.used_gb || 0;
  const ramTotal = monitorData?.memory?.total_gb || 0;
  const netOut = monitorData?.network?.out_mbps || 0;
  const netIn = monitorData?.network?.in_mbps || 0;
  const conns = monitorData?.connections?.http_active || 0;
  const connsTotal = monitorData?.connections?.established || 0;
  const streams = monitorData?.connections?.estimated_1080p_streams || 0;
  const bw = monitorData?.network?.max_bandwidth_mbps || bandwidthMbps;
  const maxStreams = Math.max(1, Math.floor(bw / 5));
  const diskPct = monitorData?.disk?.percent || 0;
  const diskFree = monitorData?.disk?.free_gb || 0;
  const diskTotal = monitorData?.disk?.total_gb || 0;
  const loadAvg = monitorData?.cpu?.load_avg_1m || 0;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px' }}>
      <MiniChartCard
        title="CPU"
        value={cpu}
        unit="%"
        subtitle={`Load: ${monitorData?.cpu?.load_avg_1m} / ${monitorData?.cpu?.load_avg_5m} / ${monitorData?.cpu?.load_avg_15m} (${monitorData?.cpu?.cores} ядер)`}
        color="#eab308"
        bgColor="#fffef5"
        borderColor="#fef08a"
        data={data}
        getSeries={getCpu}
        maxOverride={100}
        period={period}
        formatVal={v => Math.round(v) + '%'}
        thresholds={[{ value: 85, color: '#ef4444', label: '85%' }]}
      />

      <MiniChartCard
        title="Память (RAM)"
        value={ramGb}
        unit={`ГБ / ${ramTotal} ГБ`}
        subtitle={`${ram}% использовано`}
        color="#ec4899"
        bgColor="#fdf2f8"
        borderColor="#fbcfe8"
        data={data}
        getSeries={getRam}
        maxOverride={100}
        period={period}
        formatVal={v => Math.round(v) + '%'}
        thresholds={[{ value: 90, color: '#ef4444', label: '90%' }]}
      />

      <MiniChartCard
        title="Исходящий трафик"
        value={netOut}
        unit={`/ ${bw} Мбит/с (${Math.round(netOut / bw * 100)}%)`}
        subtitle={`Канал: ${bw} Мбит/с`}
        color="#0284c7"
        bgColor="#f0f9ff"
        borderColor="#bae6fd"
        data={data}
        getSeries={getNetOut}
        maxOverride={bw}
        period={period}
        formatVal={v => v.toFixed(1)}
        thresholds={[
          { value: bw * 0.7, color: '#f59e0b', label: '70%' },
          { value: bw * 0.9, color: '#ef4444', label: '90%' }
        ]}
      />

      <MiniChartCard
        title="Входящий трафик"
        value={netIn}
        unit={`/ ${bw} Мбит/с (${Math.round(netIn / bw * 100)}%)`}
        subtitle={`Канал: ${bw} Мбит/с`}
        color="#22c55e"
        bgColor="#f0fdf4"
        borderColor="#bbf7d0"
        data={data}
        getSeries={getNetIn}
        maxOverride={bw}
        period={period}
        formatVal={v => v.toFixed(1)}
        thresholds={[
          { value: bw * 0.7, color: '#f59e0b', label: '70%' },
          { value: bw * 0.9, color: '#ef4444', label: '90%' }
        ]}
      />

      <MiniChartCard
        title="HTTP соединения"
        value={conns}
        unit={`/ 1000 (${Math.round(conns / 10)}%)`}
        subtitle={`${connsTotal} всего TCP / макс ~1000 одновременных`}
        color="#f97316"
        bgColor="#fff7ed"
        borderColor="#fed7aa"
        data={data}
        getSeries={getConns}
        maxOverride={1000}
        period={period}
        formatVal={v => Math.round(v).toString()}
        thresholds={[
          { value: 750, color: '#f59e0b', label: '75%' },
          { value: 900, color: '#ef4444', label: '90%' }
        ]}
      />

      <MiniChartCard
        title="Видеопотоки (1080p)"
        value={`~${streams}`}
        unit={`/ ${maxStreams} макс (${Math.round(streams / maxStreams * 100)}%)`}
        subtitle={`~5 Мбит/с на поток, макс ${maxStreams} при канале ${bw} Мбит/с`}
        color="#8b5cf6"
        bgColor="#faf5ff"
        borderColor="#e9d5ff"
        data={data}
        getSeries={getStreams}
        maxOverride={maxStreams}
        period={period}
        formatVal={v => Math.round(v).toString()}
        thresholds={[
          { value: maxStreams * 0.7, color: '#f59e0b', label: '70%' },
          { value: maxStreams * 0.9, color: '#ef4444', label: '90%' }
        ]}
      />
      <MiniChartCard
        title='Диск данных (/var)'
        value={diskPct}
        unit={`% — свободно ${diskFree} / ${diskTotal} ГБ`}
        subtitle={`Диск загружен на ${diskPct}% (свободно ${diskFree} из ${diskTotal} ГБ)`}
        color='#0ea5e9'
        bgColor='#f0f9ff'
        borderColor='#bae6fd'
        data={data}
        getSeries={getDisk}
        maxOverride={100}
        period={period}
        formatVal={v => Math.round(v) + '%'}
        thresholds={[
          { value: 80, color: '#f59e0b', label: '80%' },
          { value: 90, color: '#ef4444', label: '90%' }
        ]}
      />
    </div>
  );
};

export default MonitorChart;
